import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showCompare: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductListProductImageStyles;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
});

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasProductContext;

export interface ProductListProductImageStyles {
    wrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    compareCheckbox?: CheckboxPresentationProps;
    addToListWrapper?: InjectableCss;
    addToListLink?: ProductAddToListLinkStyles;
    productSaveCompareWrapper?: InjectableCss;
    productImageWrapper?: InjectableCss;
    saveLink?: InjectableCss;
}

export const productImageStyles: ProductListProductImageStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 25%;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    width: 45% !important;
                `,
                null,
                null,
                null,
            ])}
        `,
    },
    compareCheckbox: {
        css: css`
            align-self: center;
            flex: 2;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            span {
                height: 12px;
                width: 12px;
                min-width: 12px !important;
                border: 1px solid ${COLORS.background.tag} !important;
            }
            label {
                font-size: ${TYPOGRAPHY.size.tiny};
                margin-left: 5px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    margin-right:10px;
                    flex:none;
                `,
                css`
                    flex:1;
                `,
                null,
                null,
                null,
            ])}
        `,
    },
    addToListWrapper: {
        css: css`
            display: inline-flex;
            flex: 1;
            // justify-content: flex-end;
            button > span > span {
                width: 16px !important;
                svg {
                    height: 10px !important;
                    width: 12px !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    flex:none;
                `,
                null,
                null,
                null,
                null,
            ])}
        `,
    },
    productSaveCompareWrapper: {
        css: css`
            display: flex;
            margin-bottom: 10px;
        `,
    },
    saveLink: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.tiny};
            line-height: 16px;
            span {
                color: ${COLORS.background.tag};
                vertical-align: middle;
            }
        `,
    },
};

const ProductListProductCompare: FC<Props> = ({
    productContext,
    showCompare,
    isProductCompareFull,
    settingsCollection,
    productCompareChecked,
    extendedStyles,
    addProductToCompare,
    removeProductFromCompare,
    showAddToList,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productImageStyles, extendedStyles));

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };
    return (
        <StyledWrapper
            {...styles.wrapper}
            data-test-selector="productListProductCompare"
            data-test-key={productContext.product.id}
        >
            <StyledWrapper {...styles.productSaveCompareWrapper}>
                {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                    <Checkbox
                        {...styles.compareCheckbox}
                        checked={productCompareChecked}
                        onChange={handleChanged}
                        data-test-selector="productList_CompareCheckbox"
                    >
                        {translate("Compare")}
                    </Checkbox>
                )}
                {showAddToList && (
                    <StyledWrapper {...styles.addToListWrapper}>
                        <ProductAddToListLink
                            data-test-selector={`actionsAddToList${productContext.product.id}`}
                            extendedStyles={styles.addToListLink}
                            {...styles.saveLink}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListProductCompare));